import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import CardTitle from '../../../common/card/CardTitle';
import { ApiParams } from '../../../common/api/api';
import WrappedTableCell from '../../../common/WrappedTableCell';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import {
  BaseProductDesignOutputCardProps,
  ProductValidation
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';
import {
  useFindProductValidationsByDesignOutputId,
  useProductEntityPrefix
} from '../Product.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignOutputAssignValidationDialog from '../dialogs/DesignOutputAssignValidationDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';

const DesignOutputValidationReferenceCard: React.FC<BaseProductDesignOutputCardProps> =
  (props: BaseProductDesignOutputCardProps) => {
    const {
      productId,
      productDesignOutput,
      productDesignOutputVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productValidationList,
      isLoadingProductValidations,
      loadingProductValidationsError,
      findProductValidations
    } = useFindProductValidationsByDesignOutputId(
      productId,
      productDesignOutput.id
    );
    const { isFieldDisabled } = useTaskEntityFields(productDesignOutput.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.Validation
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };
    const findProductValidationsWithParams = useCallback(() => {
      const params: ApiParams = { getFromRelation: true };
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productDesignOutputVersion)
        params.version = productDesignOutputVersion.version;
      findProductValidations(params);
    }, [
      findProductValidations,
      order,
      orderDirection,
      productDesignOutputVersion
    ]);

    useEffect(() => {
      findProductValidationsWithParams();
    }, [findProductValidationsWithParams]);

    useEffect(() => {
      if (loadingProductValidationsError) {
        onError?.(loadingProductValidationsError);
      }
    }, [loadingProductValidationsError]);

    const disabled =
      Boolean(productDesignOutputVersion) ||
      isFieldDisabled('assignValidation');

    return (
      <Card>
        <CardTitle>
          <AssignmentIcon />
          {getProductEntityName('validation')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productValidation.id'}
                    direction={getSortDirection('productValidation.id')}
                    onClick={() => handleSort('productValidation.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productValidation.title'}
                    direction={getSortDirection('productValidation.title')}
                    onClick={() => handleSort('productValidation.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductValidations && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productValidationList?.map((validation: ProductValidation) => (
                <TableRow
                  hover
                  key={validation.id}
                  onClick={() => {
                    history.push(
                      `/portal/products/${productId}/validations/${validation.id}`
                    );
                  }}
                >
                  <TableCell>{prefix + validation.id}</TableCell>
                  <WrappedTableCell>{validation.title}</WrappedTableCell>
                  <TableCell>
                    <TaskStatusLabel task={validation.task} />
                  </TableCell>
                  <TableCell
                    style={{
                      textAlign: 'center'
                    }}
                  >
                    {!disabled && (
                      <CloseIcon
                        onClick={event => {
                          event.stopPropagation();
                          setItemToUnlink(validation.id);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign {getProductEntityName('validation')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <DesignOutputAssignValidationDialog
            open={openDialog}
            productId={productId}
            designOutputId={productDesignOutput.id}
            selectedItems={productValidationList}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductValidationsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productDesignOutput.id}
          from={ProductBoardColumnEntity.DesignOutput}
          to={ProductBoardColumnEntity.Validation}
          unlinkItemId={itemToUnlink}
          selectedItems={productValidationList}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            findProductValidationsWithParams();
            onUpdate?.();
            setItemToUnlink(undefined);
          }}
        />
      </Card>
    );
  };

export default DesignOutputValidationReferenceCard;
